import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const useDailyXP = (userId: string | undefined) => {
  const hasCheckedToday = useRef(false);
  
  useEffect(() => {
    const checkAndAwardDailyXP = async () => {
      if (!userId || hasCheckedToday.current) return;

      try {
        // Check last login date from user_streaks table
        const { data: streakData } = await supabase
          .from('user_streaks')
          .select('last_action, current_streak')
          .eq('user_id', userId)
          .maybeSingle();

        const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
        const lastAction = streakData?.last_action;

        // If user hasn't logged in today, award streak XP
        if (lastAction !== today) {
          // Update streak and get new streak value
          const { data: newStreak, error: streakError } = await supabase.rpc('update_user_streak', {
            target_user_id: userId
          });

          if (streakError) throw streakError;

          // Award daily XP (25 base + bonus for longer streaks)
          let dailyXP = 25;
          const currentStreak = newStreak || 1;
          
          // Bonus XP for longer streaks
          if (currentStreak >= 30) dailyXP += 50; // 30+ days: +50 XP bonus
          else if (currentStreak >= 14) dailyXP += 25; // 14+ days: +25 XP bonus
          else if (currentStreak >= 7) dailyXP += 10; // 7+ days: +10 XP bonus

          // Award the daily XP
          const { error: xpError } = await supabase.rpc('award_xp', {
            target_user_id: userId,
            amount: dailyXP
          });

          if (xpError) throw xpError;

          // Show congratulatory message
          let message = `Daily login! +${dailyXP} XP earned!`;
          if (currentStreak > 1) {
            message += ` ${currentStreak} day streak! 🔥`;
          }

          toast.success(message, {
            duration: 6000,
          });
        }

        // Mark as checked for this session
        hasCheckedToday.current = true;
      } catch (error) {
        console.error('Error checking daily XP:', error);
      }
    };

    checkAndAwardDailyXP();
  }, [userId]);
};